import { useMemo } from 'react';

type AuctionImage = {
  file?: {
    src?: string | null;
    name?: string | null;
  } | null;
};

interface AuctionImagesProps {
  images: AuctionImage[];
}

// Helper function to generate optimized image URL
const getOptimizedImageUrl = (src: string, width: number, height: number) => {
  if (!src) return '/images/placeholder-image-600x400.png';

  if (src.includes('imagedelivery.net') || src.startsWith('http')) {
    return src.includes('?')
      ? `${src}&w=${width}&h=${height}&format=webp&fit=cover`
      : `${src}?w=${width}&h=${height}&format=webp&fit=cover`;
  }
  return src;
};

// Helper function to generate srcSet
const getSrcSet = (src: string, width: number, height: number) => {
  if (!src || !src.includes('imagedelivery.net')) return undefined;

  const baseUrl = src.split('?')[0];
  return [
    `${baseUrl}?w=${width}&h=${height}&format=webp&fit=cover ${width}w`,
    `${baseUrl}?w=${width * 2}&h=${height * 2}&format=webp&fit=cover ${width * 2}w`,
  ].join(', ');
};

export const AuctionImages = ({ images }: AuctionImagesProps) => {
  // Memoize optimized URLs to prevent recalculation on every render
  const optimizedImages = useMemo(() => {
    return images.map((img) => ({
      ...img,
      optimizedSrc: getOptimizedImageUrl(img?.file?.src || '', 400, 300),
      srcSet: getSrcSet(img?.file?.src || '', 400, 300),
    }));
  }, [images]);

  return (
    <div>
      {images.length < 3 ? (
        <div className="flex h-56 w-full items-center justify-center">
          <img
            src={optimizedImages[0]?.optimizedSrc || '/images/placeholder-image-600x400.png'}
            srcSet={optimizedImages[0]?.srcSet}
            sizes="(max-width: 768px) 100vw, 600px"
            width={600}
            height={400}
            loading="lazy"
            decoding="async"
            alt={images[0]?.file?.name || 'Auction image'}
            className="h-56 w-full object-cover"
          />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-0.5">
          <div className="col-span-2 flex h-56 items-center justify-center">
            <img
              src={optimizedImages[0]?.optimizedSrc || '/images/placeholder-image-600x400.png'}
              srcSet={optimizedImages[0]?.srcSet}
              sizes="(max-width: 768px) 66vw, 400px"
              width={400}
              height={224}
              loading="lazy"
              decoding="async"
              alt={images[0]?.file?.name || 'Auction image'}
              className="h-full md:h-56 md:w-full md:object-cover"
            />
          </div>
          <div className="flex basis-2/6 flex-col space-y-[1px] md:basis-1/3">
            {optimizedImages.slice(1, 3).map((img, index) => (
              <img
                key={`${img.file?.name || 'img'}-${index}`}
                src={img.optimizedSrc || '/images/placeholder-image-600x400.png'}
                srcSet={img.srcSet}
                sizes="(max-width: 768px) 33vw, 200px"
                width={200}
                height={112}
                loading="lazy"
                decoding="async"
                alt={img?.file?.name || `Auction image ${index + 2}`}
                className="object-cover md:h-[112px]"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
